import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import UniversalTabContainer from './UniversalTabContainer';
import SystemStatus from './SystemStatus';
import Tooltip from './Tooltip';
import PauseSettings from './PauseSettings';
import ContributionPopup from './ContributionPopup';
import { isNativePlatform } from '../services/biometricAuth';
import './UnifiedPreferences.css';
import { 
  getUserPreferences, 
  updateUserPreferences, 
  changePassword, 
  notifications,
  requestAccountDeletion,
  exportAccountData
} from '../api';
import api, { createApiInstance } from '../api';
import logger from '../utils/logger';
import { 
  requestNotificationPermission, 
  getNotificationPermission,
  sendTestNotification
} from '../services/pushNotificationService';

const prefsApi = createApiInstance();

const UnifiedPreferences = ({ includeAdminTab = false, adminOnlyMode = false }) => {
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Get initial tab from URL parameter
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return tab || 'account';
  };

  const [defaultTab] = useState(adminOnlyMode ? 'admin' : getInitialTab());

  // Account Settings State
  const [selectedTheme, setSelectedTheme] = useState('light-blue');
  const [selectedHomePage, setSelectedHomePage] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);

  // Admin Settings State
  const [ticketDeleteDays, setTicketDeleteDays] = useState(30);
  const [profileViewHistoryDays, setProfileViewHistoryDays] = useState(7);
  const [enableL3V3LForAll, setEnableL3V3LForAll] = useState(true);
  const [profilePictureAlwaysVisible, setProfilePictureAlwaysVisible] = useState(true);
  const [savingSystemSettings, setSavingSystemSettings] = useState(false);
  const [savedSystemSettings, setSavedSystemSettings] = useState(null);
  const [adminSettingsLoading, setAdminSettingsLoading] = useState(false);
  
  // Contribution Settings State
  const [contributionEnabled, setContributionEnabled] = useState(false);
  const [resetArmed, setResetArmed] = useState(false);
  const [savingContributionSettings, setSavingContributionSettings] = useState(false);
  
  // User Contribution Management State
  const [contributionHistory, setContributionHistory] = useState([]);
  const [loadingContributionHistory, setLoadingContributionHistory] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Notification Settings State
  const [notificationPreferences, setNotificationPreferences] = useState(null);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Privacy Settings State (Premium feature)
  const [userRole, setUserRole] = useState('free_user');
  const [privacySettings, setPrivacySettings] = useState({
    hideFavorites: false,
    hideShortlist: false,
    hideProfileViews: false
  });

  // Daily Digest Settings State
  const [digestSettings, setDigestSettings] = useState({
    enabled: false,
    minMatchScore: 0,
    skipIfNoActivity: true,
    batchFavorites: true,
    batchShortlists: true,
    batchProfileViews: true,
    batchPiiRequests: false,
    batchNewMatches: true
  });

  // Push Notification State
  const [pushPermission, setPushPermission] = useState('default');
  const [testingPush, setTestingPush] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);

  // Pause Settings State
  const [pauseStatus, setPauseStatus] = useState(null);
  const [showPauseModal, setShowPauseModal] = useState(false);

  // Account Deletion State
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [downloadData, setDownloadData] = useState(false);

  // MFA Settings State
  const [mfaStatus, setMfaStatus] = useState({
    mfa_enabled: false,
    mfa_channel: null,
    contact_masked: null,
    backup_codes_count: 0
  });
  const [smsOptIn, setSmsOptIn] = useState(true); // Track SMS opt-in status
  const [shareContactWithPaidMembers, setShareContactWithPaidMembers] = useState(true);
  const [hasPhoneNumber, setHasPhoneNumber] = useState(false); // Track if user has phone number
  const [selectedMfaMethod, setSelectedMfaMethod] = useState('none');
  const [mfaStep, setMfaStep] = useState('select'); // 'select', 'verify', 'backup_codes'
  const [mfaVerificationCode, setMfaVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [mfaMessage, setMfaMessage] = useState({ type: '', text: '' });
  const [showDisableMfaModal, setShowDisableMfaModal] = useState(false);
  const [disableMfaPassword, setDisableMfaPassword] = useState('');

  const themes = [
    {
      id: 'light-blue',
      name: 'Cozy Light',
      icon: '☀️',
      description: 'Warm and inviting light theme with soft colors',
      preview: { primary: '#7c3aed', secondary: '#c084fc', background: '#fff8ef', text: '#3a2614' }
    },
    {
      id: 'sky-blue',
      name: 'Sky Blue',
      icon: '🩵',
      description: 'Calm light blue theme with airy sky tones',
      preview: { primary: '#0ea5e9', secondary: '#7dd3fc', background: '#e3f4ff', text: '#0f3d56' }
    },
    {
      id: 'dark',
      name: 'Cozy Night',
      icon: '🌙',
      description: 'Warm purple dark mode for comfortable evening browsing',
      preview: { primary: '#a78bfa', secondary: '#c4b5fd', background: '#1a1625', text: '#e5e7eb' }
    },
    {
      id: 'light-pink',
      name: 'Rose Garden',
      icon: '🌹',
      description: 'Romantic rose-tinted theme',
      preview: { primary: '#ec4899', secondary: '#f9a8d4', background: '#fff5f7', text: '#374151' }
    },
    {
      id: 'light-gray',
      name: 'Minimal Gray',
      icon: '⚪',
      description: 'Clean and minimal light gray theme',
      preview: { primary: '#6b7280', secondary: '#9ca3af', background: '#f9fafb', text: '#1f2937' }
    },
    {
      id: 'ultra-light-gray',
      name: 'Ultra Light',
      icon: '🤍',
      description: 'Ultra minimal white theme',
      preview: { primary: '#94a3b8', secondary: '#cbd5e1', background: '#5e5e6a', text: '#f3f4f6' }
    },
    {
      id: 'ultra-light-green',
      name: 'Fresh Green',
      icon: '🌿',
      description: 'Clean and fresh light green theme',
      preview: { primary: '#10b981', secondary: '#34d399', background: '#f0fdf4', text: '#064e3b' }
    },
    {
      id: 'ultra-black',
      name: 'Midnight',
      icon: '🌑',
      description: 'Pure black OLED theme with silver accents',
      preview: { primary: '#1e3a5f', secondary: '#264d73', background: '#000000', text: '#e5e5e5' }
    },
    {
      id: 'indian-wedding',
      name: 'Indian Celebration',
      icon: '🪔',
      description: 'Vibrant marigold and gold celebrating Indian weddings',
      preview: { primary: '#ff6b35', secondary: '#f7931e', background: '#fffaf0', text: '#7c2d12' }
    },
    {
      id: 'newspaper',
      name: 'The Daily News',
      icon: '📰',
      description: 'Classic black and white newspaper style',
      preview: { primary: '#1a1a1a', secondary: '#4a4a4a', background: '#f4f1ea', text: '#1a1a1a' }
    },
    {
      id: 'cute-bubble',
      name: 'Cute Bubble',
      icon: '🫧',
      description: 'Playful purple-pink theme with bubble effects',
      preview: { primary: '#8b5cf6', secondary: '#ec4899', background: '#f8f7ff', text: '#374151' }
    },
    {
      id: 'clean-connect',
      name: 'Clean Connect',
      icon: '🕊️',
      description: 'Crisp white and gray minimal theme',
      preview: { primary: '#F8FCFF', secondary: '#9ca3af', background: '#f4f5f7', text: '#1f2937' }
    },
    {
      id: 'white',
      name: 'Egg White',
      icon: '🥚',
      description: 'Egg-white background with blue-white combination',
      preview: { primary: '#f5c97a', secondary: '#ffe3b3', background: '#fffdf7', text: '#2f3445', accent: '#7fb4ff' }
    }
  ];

  const notificationTriggers = {
    matches: [
      { id: 'new_match', label: 'New Match', description: 'Someone matches with you' },
      { id: 'mutual_favorite', label: 'Mutual Favorite', description: 'You both favorited each other' },
      { id: 'shortlist_added', label: 'Shortlist Added', description: 'Someone adds you to their shortlist' },
      { id: 'match_milestone', label: 'Match Milestone', description: 'Match anniversaries' }
    ],
    activity: [
      { id: 'profile_view', label: 'Profile Views', description: 'Someone views your profile' },
      { id: 'favorited', label: 'Favorited', description: 'Someone favorites you' },
      { id: 'profile_visibility_spike', label: 'Visibility Spike', description: 'Unusual traffic' },
      { id: 'search_appearance', label: 'Search Appearances', description: 'Profile in search results' }
    ],
    messages: [
      { id: 'new_message', label: 'New Messages', description: 'New message received' },
      { id: 'message_read', label: 'Message Read', description: 'Your message was read' },
      { id: 'conversation_cold', label: 'Conversation Reminder', description: 'No reply in 3 days' }
    ],
    privacy: [
      { id: 'pii_request', label: 'PII Request', description: 'Someone requests access' },
      { id: 'pending_pii_request', label: 'Pending Request Reminder', description: 'Reminder about unanswered PII requests' },
      { id: 'pii_granted', label: 'PII Granted', description: 'Access request approved' },
      { id: 'pii_denied', label: 'PII Denied', description: 'Access request denied' },
      { id: 'pii_expiring', label: 'PII Expiring', description: 'Access expiring in 24h' },
      { id: 'suspicious_login', label: 'Suspicious Login', description: 'Unusual login detected' }
    ],
    engagement: [
      { id: 'unread_messages', label: 'Unread Messages', description: 'You have unread messages' },
      { id: 'new_users_matching', label: 'New Users', description: 'New users match criteria' },
      { id: 'profile_incomplete', label: 'Complete Profile', description: 'Profile completion reminder' },
      { id: 'upload_photos', label: 'Upload Photos', description: 'Photo upload reminder' }
    ],
    digests: [
      { id: 'daily_digest', label: 'Daily Digest', description: 'Daily activity summary email (configure in Daily Digest section below)' },
      { id: 'weekly_digest', label: 'Weekly Digest', description: 'Weekly activity summary email' },
      { id: 'monthly_digest', label: 'Monthly Digest', description: 'Monthly activity report with charts' },
      { id: 'saved_search_matches', label: 'Saved Search Matches', description: 'New profiles matching your saved searches' }
    ],
    polls: [
      { id: 'poll_reminder', label: 'Poll Reminder', description: 'Reminder to respond to active polls' }
    ]
  };

  const activityTriggerIds = new Set(
    (notificationTriggers.activity || []).map((trigger) => trigger.id)
  );

  const isActivityChannelBlocked = (triggerId, channel) => (
    activityTriggerIds.has(triggerId)
    && (channel === 'email' || channel === 'sms')
  );

  const sanitizeActivityChannels = (channelsMap = {}) => {
    const sanitizedChannels = { ...channelsMap };
    activityTriggerIds.forEach((triggerId) => {
      const existingChannels = sanitizedChannels[triggerId];
      if (Array.isArray(existingChannels)) {
        sanitizedChannels[triggerId] = existingChannels.filter(
          (channel) => channel !== 'email' && channel !== 'sms'
        );
      }
    });

    return sanitizedChannels;
  };

  const channels = ['email', 'sms', 'push'];

  // Load account preferences
  useEffect(() => {
    const loadAccountPreferences = async () => {
      try {
        const userPrefs = await getUserPreferences();
        if (userPrefs.themePreference) {
          setSelectedTheme(userPrefs.themePreference);
        }
        if (userPrefs.homePage) {
          setSelectedHomePage(userPrefs.homePage);
        }
      } catch (error) {
        logger.error('Error loading preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccountPreferences();
  }, []);

  // Load notification preferences
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      try {
        setLoadingNotifications(true);
        const data = await notifications.getPreferences();
        const sanitizedChannels = sanitizeActivityChannels(data.channels || {});
        setNotificationPreferences({
          ...data,
          channels: sanitizedChannels,
        });
        
        // Load privacy settings from notification preferences
        if (data.privacySettings) {
          setPrivacySettings({
            hideFavorites: data.privacySettings.hideFavorites || false,
            hideShortlist: data.privacySettings.hideShortlist || false,
            hideProfileViews: data.privacySettings.hideProfileViews || false
          });
        }
        
        // Load digest settings from notification preferences
        if (data.digestSettings) {
          setDigestSettings(prev => ({
            ...prev,
            enabled: data.digestSettings.enabled || false,
            minMatchScore: data.digestSettings.minMatchScore || 0,
            skipIfNoActivity: data.digestSettings.skipIfNoActivity !== false,
            batchFavorites: data.digestSettings.batchFavorites !== false,
            batchShortlists: data.digestSettings.batchShortlists !== false,
            batchProfileViews: data.digestSettings.batchProfileViews !== false,
            batchPiiRequests: data.digestSettings.batchPiiRequests || false,
            batchNewMatches: data.digestSettings.batchNewMatches !== false
          }));
        }
      } catch (error) {
        logger.error('Error loading notification preferences:', error);
        showToast('Failed to load notification preferences', 'error');
      } finally {
        setLoadingNotifications(false);
      }
    };
    
    const fetchSmsOptInStatus = async () => {
      try {
        const username = localStorage.getItem('username');
        const response = await prefsApi.get(`/api/users/profile/${username}`);
        if (response.data) {
          if (response.data.smsOptIn !== undefined) {
            setSmsOptIn(response.data.smsOptIn);
          }
          setShareContactWithPaidMembers(response.data.shareContactWithPaidMembers !== false);
          // Check if user has a phone number (not null, not empty, not masked)
          const phoneNumber = response.data.contactNumber;
          const hasPhone = phoneNumber && phoneNumber.trim() !== '' && !phoneNumber.includes('***');
          setHasPhoneNumber(hasPhone);
        }
      } catch (error) {
        logger.error('Error loading SMS opt-in status:', error);
      }
    };

    const checkAdminStatus = () => {
      const username = localStorage.getItem('username');
      const role = localStorage.getItem('userRole');
      setIsAdmin(role === 'admin');
      setUserRole(role || 'free_user');
    };

    fetchNotificationPreferences();
    fetchSmsOptInStatus();
    checkAdminStatus();
    loadPauseStatus();
    
    // Check push notification status
    setPushPermission(getNotificationPermission());
  }, []);

  // Load MFA status
  useEffect(() => {
    const fetchMfaStatus = async () => {
      try {
        const response = await prefsApi.get('/api/auth/mfa/status');
        setMfaStatus(response.data);
        if (response.data.mfa_enabled) {
          setSelectedMfaMethod(response.data.mfa_channel || 'email');
        }
      } catch (error) {
        logger.error('Error loading MFA status:', error);
      }
    };

    fetchMfaStatus();
  }, []);

  const loadAdminSettings = async () => {
    try {
      setAdminSettingsLoading(true);
      const response = await api.get('/system-settings');
      const loaded = {
        ticket_delete_days: response.data.ticket_delete_days ?? 30,
        profile_view_history_days: response.data.profile_view_history_days ?? 7,
        enable_l3v3l_for_all: response.data.enable_l3v3l_for_all ?? true,
        profile_picture_always_visible: response.data.profile_picture_always_visible ?? true
      };
      setTicketDeleteDays(loaded.ticket_delete_days);
      setProfileViewHistoryDays(loaded.profile_view_history_days);
      setEnableL3V3LForAll(loaded.enable_l3v3l_for_all);
      setProfilePictureAlwaysVisible(loaded.profile_picture_always_visible);
      setSavedSystemSettings(loaded);
    } catch (error) {
      logger.error('Error loading admin settings:', error);
    } finally {
      setAdminSettingsLoading(false);
    }
  };

  const systemSettingsDirty = savedSystemSettings !== null && (
    ticketDeleteDays !== savedSystemSettings.ticket_delete_days ||
    profileViewHistoryDays !== savedSystemSettings.profile_view_history_days ||
    enableL3V3LForAll !== savedSystemSettings.enable_l3v3l_for_all ||
    profilePictureAlwaysVisible !== savedSystemSettings.profile_picture_always_visible
  );

  const handleDiscardSystemSettings = () => {
    if (!savedSystemSettings) return;
    setTicketDeleteDays(savedSystemSettings.ticket_delete_days);
    setProfileViewHistoryDays(savedSystemSettings.profile_view_history_days);
    setEnableL3V3LForAll(savedSystemSettings.enable_l3v3l_for_all);
    setProfilePictureAlwaysVisible(savedSystemSettings.profile_picture_always_visible);
  };

  const handleSaveSystemSettings = async () => {
    try {
      setSavingSystemSettings(true);

      const payload = {
        ticket_delete_days: ticketDeleteDays,
        profile_view_history_days: profileViewHistoryDays,
        enable_l3v3l_for_all: enableL3V3LForAll,
        profile_picture_always_visible: profilePictureAlwaysVisible
      };
      await api.put('/system-settings', payload);

      setSavedSystemSettings(payload);
      showToast('System settings saved successfully!', 'success');
    } catch (error) {
      logger.error('Error saving system settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSavingSystemSettings(false);
    }
  };

  // Contribution Settings Functions
  const loadContributionSettings = async () => {
    try {
      const response = await prefsApi.get('/api/contributions/admin/contribution-settings');
      if (response.data.success) {
        const settings = response.data.contributions;
        setContributionEnabled(settings.enabled || false);
      }
    } catch (error) {
      logger.error('Error loading contribution settings:', error);
    }
  };

  const handleSaveContributionSettings = async () => {
    try {
      setSavingContributionSettings(true);
      await prefsApi.put('/api/contributions/admin/contribution-settings', {
        enabled: contributionEnabled
      });
      showToast('Contribution settings saved successfully!', 'success');
    } catch (error) {
      logger.error('Error saving contribution settings:', error);
      showToast('Failed to save contribution settings', 'error');
    } finally {
      setSavingContributionSettings(false);
    }
  };

  // Load user contribution history
  const loadContributionHistory = async () => {
    try {
      setLoadingContributionHistory(true);
      const response = await prefsApi.get('/api/contributions/contribution-history');
      
      if (response.data.success) {
        setContributionHistory(response.data.contributions || []);
      }
    } catch (error) {
      logger.error('Error loading contribution history:', error);
      // Don't show error toast for this - it's not critical
    } finally {
      setLoadingContributionHistory(false);
    }
  };

  useEffect(() => {
    if (isAdmin && includeAdminTab) {
      loadAdminSettings();
      loadContributionSettings();
    }
    // Load user contribution data for all users
    loadContributionHistory();
  }, [isAdmin, includeAdminTab]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Pause feature functions
  const loadPauseStatus = async () => {
    try {
      const response = await prefsApi.get('/api/account/pause-status');
      setPauseStatus(response.data);
    } catch (error) {
      logger.error('Error loading pause status:', error);
    }
  };

  const handlePauseSuccess = async (data) => {
    showToast('Your profile has been paused successfully', 'success');
    await loadPauseStatus();
  };

  const handleUnpause = async () => {
    try {
      await prefsApi.post('/api/account/unpause', {});
      showToast('Welcome back! Your profile is now active.', 'success');
      await loadPauseStatus();
    } catch (error) {
      logger.error('Error unpausing account:', error);
      showToast('Failed to unpause account', 'error');
    }
  };

  // Theme change handler
  const handleThemeChange = async (themeId) => {
    setSelectedTheme(themeId);
    // Apply theme to body element (CSS uses body.theme-*)
    document.body.className = `theme-${themeId}`;
    
    try {
      await updateUserPreferences({ themePreference: themeId });
      logger.debug('Theme saved to database:', themeId);
      showToast('Theme updated successfully!', 'success');
    } catch (error) {
      logger.error('Error saving theme:', error);
      showToast('Failed to save theme preference', 'error');
    }
  };

  // Home page change handler
  const handleHomePageChange = async (pageId) => {
    setSelectedHomePage(pageId);
    
    try {
      await updateUserPreferences({ homePage: pageId });
      // Also save to localStorage for login redirect
      localStorage.setItem('homePage', pageId);
      logger.debug('Home page saved:', pageId);
      showToast('Home page updated successfully!', 'success');
    } catch (error) {
      logger.error('Error saving home page:', error);
      showToast('Failed to save home page preference', 'error');
    }
  };

  // Password validation helper
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter (A-Z)');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('At least one lowercase letter (a-z)');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('At least one number (0-9)');
    }
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
      errors.push('At least one special character (!@#$%^&*...)');
    }
    return errors;
  };

  // Get password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: '', color: '' };
    const errors = validatePassword(password);
    const strength = 5 - errors.length;
    if (strength <= 1) return { level: 1, label: 'Weak', color: '#e74c3c' };
    if (strength <= 2) return { level: 2, label: 'Fair', color: '#f39c12' };
    if (strength <= 3) return { level: 3, label: 'Good', color: '#3498db' };
    if (strength <= 4) return { level: 4, label: 'Strong', color: '#27ae60' };
    return { level: 5, label: 'Excellent', color: '#2ecc71' };
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate new password meets requirements
    const passwordErrors = validatePassword(passwordData.newPassword);
    if (passwordErrors.length > 0) {
      setPasswordMessage({ 
        type: 'error', 
        text: `Password requirements not met:\n• ${passwordErrors.join('\n• ')}` 
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (!passwordData.currentPassword) {
      setPasswordMessage({ type: 'error', text: 'Please enter your current password' });
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      await changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword
      });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showToast('Password changed successfully!', 'success');
    } catch (error) {
      // Parse error message from backend
      let errorMessage = 'Failed to change password';
      if (error.detail) {
        errorMessage = error.detail;
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      setPasswordMessage({ type: 'error', text: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Notification handlers
  const handleChannelToggle = (triggerId, channel) => {
    if (isActivityChannelBlocked(triggerId, channel)) {
      return;
    }

    const currentChannels = notificationPreferences.channels[triggerId] || [];
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];

    setNotificationPreferences({
      ...notificationPreferences,
      channels: {
        ...notificationPreferences.channels,
        [triggerId]: newChannels
      }
    });
  };

  const handleQuietHoursToggle = () => {
    setNotificationPreferences({
      ...notificationPreferences,
      quietHours: {
        ...notificationPreferences.quietHours,
        enabled: !notificationPreferences.quietHours.enabled
      }
    });
  };

  const handleQuietHoursChange = (field, value) => {
    setNotificationPreferences({
      ...notificationPreferences,
      quietHours: {
        ...notificationPreferences.quietHours,
        [field]: value
      }
    });
  };

  const handlePrivacyToggle = (setting) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };


  const handleShareContactToggle = async () => {
    const value = !shareContactWithPaidMembers;
    setShareContactWithPaidMembers(value);
    try {
      const username = localStorage.getItem('username');
      const data = new FormData();
      data.append('shareContactWithPaidMembers', value);
      await api.put(`/profile/${username}`, data);
      showToast(value ? 'Contact info shared with paid members' : 'Paid members now require manual approval', 'success');
    } catch (err) {
      setShareContactWithPaidMembers(!value);
      showToast('Failed to update setting', 'error');
      logger.error('shareContactWithPaidMembers save failed:', err);
    }
  };
  const handleDigestToggle = (setting) => {
    setDigestSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSaveNotifications = async () => {
    try {
      setSavingNotifications(true);
      const sanitizedChannels = sanitizeActivityChannels(notificationPreferences.channels);
      await notifications.updatePreferences({
        channels: sanitizedChannels,
        quietHours: notificationPreferences.quietHours,
        privacySettings: privacySettings,
        digestSettings: digestSettings
      });
      setNotificationPreferences({
        ...notificationPreferences,
        channels: sanitizedChannels,
      });
      showToast('Notification preferences saved successfully!', 'success');
    } catch (error) {
      logger.error('Error saving notification preferences:', error);
      showToast('Failed to save notification preferences', 'error');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleResetNotifications = async () => {
    if (!resetArmed) {
      setResetArmed(true);
      setTimeout(() => setResetArmed(false), 3000);
      return;
    }
    setResetArmed(false);

    try {
      setSavingNotifications(true);
      const data = await notifications.resetPreferences();
      const resetPreferences = data?.data || data;
      const sanitizedChannels = sanitizeActivityChannels(resetPreferences.channels || {});
      setNotificationPreferences({
        ...resetPreferences,
        channels: sanitizedChannels,
      });
      showToast('Notification preferences reset to defaults', 'success');
    } catch (error) {
      logger.error('Error resetting preferences:', error);
      showToast('Failed to reset preferences', 'error');
    } finally {
      setSavingNotifications(false);
    }
  };

  // Push Notification Handlers
  const handleEnablePush = async () => {
    try {
      setEnablingPush(true);
      const token = await requestNotificationPermission();
      if (token) {
        setPushPermission('granted');
        showToast('Push notifications enabled! You will now receive browser notifications.', 'success');
      } else {
        const newPermission = getNotificationPermission();
        setPushPermission(newPermission);
        if (newPermission === 'denied') {
          showToast('Push notifications blocked. Please enable in browser settings.', 'error');
        } else {
          showToast('Push notification setup was cancelled.', 'info');
        }
      }
    } catch (error) {
      logger.error('Error enabling push notifications:', error);
      showToast('Failed to enable push notifications', 'error');
    } finally {
      setEnablingPush(false);
    }
  };

  const handleTestPush = async () => {
    try {
      setTestingPush(true);
      const result = await sendTestNotification();
      if (result.success) {
        showToast(`Test notification sent! ${result.message}`, 'success');
      } else {
        showToast(result.message || 'No active push subscriptions found', 'warning');
      }
    } catch (error) {
      logger.error('Error sending test push:', error);
      showToast('Failed to send test notification. Make sure push is enabled.', 'error');
    } finally {
      setTestingPush(false);
    }
  };

  // MFA Handlers
  const handleMfaMethodChange = (method) => {
    if (mfaStatus.mfa_enabled) {
      showToast('Please disable MFA before changing methods', 'info');
      return;
    }
    setSelectedMfaMethod(method);
    setMfaMessage({ type: '', text: '' });
  };

  const handleSendMfaCode = async () => {
    if (selectedMfaMethod === 'none') {
      setMfaMessage({ type: 'error', text: 'Please select Email or SMS' });
      return;
    }

    try {
      setLoadingMfa(true);
      setMfaMessage({ type: '', text: '' });

      const response = await prefsApi.post('/api/auth/otp/send', 
        { channel: selectedMfaMethod }
      );

      if (response.data.success) {
        setMfaStep('verify');
        setMfaMessage({ 
          type: 'success', 
          text: `Verification code sent to ${response.data.contact_masked || 'your ' + selectedMfaMethod}` 
        });
      } else if (response.data.mock_code) {
        setMfaStep('verify');
        setMfaMessage({ 
          type: 'warning', 
          text: `DEV MODE: Use code ${response.data.mock_code}` 
        });
      }
    } catch (error) {
      logger.error('Error sending MFA code:', error);
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to send code';
      setMfaMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoadingMfa(false);
    }
  };

  const handleVerifyAndEnableMfa = async () => {
    if (!mfaVerificationCode || mfaVerificationCode.length !== 6) {
      setMfaMessage({ type: 'error', text: 'Please enter the 6-digit code' });
      return;
    }

    try {
      setLoadingMfa(true);
      setMfaMessage({ type: '', text: '' });

      const response = await prefsApi.post('/api/auth/mfa/enable',
        {
          verification_code: mfaVerificationCode,
          channel: selectedMfaMethod
        }
      );

      if (response.data.codes) {
        setBackupCodes(response.data.codes);
        setMfaStep('backup_codes');
        setMfaMessage({ type: 'success', text: 'MFA enabled successfully!' });
        
        // Refresh MFA status
        const statusResponse = await prefsApi.get('/api/auth/mfa/status');
        setMfaStatus(statusResponse.data);
      }
    } catch (error) {
      logger.error('Error enabling MFA:', error);
      setMfaMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to enable MFA' });
    } finally {
      setLoadingMfa(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!disableMfaPassword) {
      setMfaMessage({ type: 'error', text: 'Please enter your password' });
      return;
    }

    try {
      setLoadingMfa(true);
      setMfaMessage({ type: '', text: '' });

      await prefsApi.post('/api/auth/mfa/disable',
        { password: disableMfaPassword }
      );

      setMfaStatus({
        mfa_enabled: false,
        mfa_channel: null,
        contact_masked: null,
        backup_codes_count: 0
      });
      setSelectedMfaMethod('none');
      setShowDisableMfaModal(false);
      setDisableMfaPassword('');
      setMfaMessage({ type: 'success', text: 'MFA disabled successfully' });
      showToast('MFA has been disabled', 'success');
    } catch (error) {
      logger.error('Error disabling MFA:', error);
      setMfaMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to disable MFA' });
    } finally {
      setLoadingMfa(false);
    }
  };

  const handleFinishMfaSetup = () => {
    setMfaStep('select');
    setMfaVerificationCode('');
    setBackupCodes([]);
    showToast('MFA setup complete!', 'success');
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    showToast('Backup codes copied to clipboard', 'success');
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    showToast('Backup codes downloaded', 'success');
  };

  // Account Deletion Handlers
  const handleRequestDeletion = async () => {
    try {
      const response = await requestAccountDeletion(deletionReason, downloadData);
      
      const scheduledDate = new Date(response.data.scheduledDate).toLocaleDateString();
      showToast(
        `Account deletion scheduled for ${scheduledDate}. You have 30 days to change your mind. Simply log back in to reactivate.`,
        'info'
      );
      
      setShowDeleteModal(false);
      
      // Redirect to login after short delay
      setTimeout(() => {
        localStorage.clear();
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      showToast('Error requesting deletion: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const handleDownloadData = async () => {
    try {
      const response = await exportAccountData();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `account-data-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Data exported successfully', 'success');
    } catch (error) {
      showToast('Error downloading data: ' + error.message, 'error');
    }
  };

  if (isLoading || loadingNotifications) {
    return (
      <div className="unified-preferences-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-preferences-container">

      <UniversalTabContainer
        variant="pills"
        defaultTab={adminOnlyMode ? 'admin' : defaultTab}
        hideNav={adminOnlyMode}
        tabs={[
          ...(!adminOnlyMode ? [
          {
            id: 'account',
            icon: '👤',
            label: 'Settings',
            content: (
              <div className="account-settings">
          {/* Home Page Selection */}
          <section className="settings-section my-home-page-section">
            <button
              type="button"
              className="btn-secondary apk-download-mini-btn"
              onClick={() => navigate('/android-apk')}
              aria-label="Download Android APK"
              title="Download Android APK"
            >
              📲
            </button>
            <h2>🏠 My Home Page</h2>
            <p className="section-description">Choose which page to show after login</p>
            
            <div className="home-page-selector">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: '📊', description: 'Overview with stats and activity' },
                { id: 'search', label: 'Search', icon: '🔍', description: 'Browse profiles directly' },
                { id: 'messages', label: 'Messages', icon: '💬', description: 'Jump to conversations' }
              ].map((page) => (
                <div
                  key={page.id}
                  className={`home-page-option ${selectedHomePage === page.id ? 'selected' : ''}`}
                  onClick={() => handleHomePageChange(page.id)}
                >
                  <span className="option-icon">{page.icon}</span>
                  <div className="option-content">
                    <span className="option-label">{page.label}</span>
                    <span className="option-description">{page.description}</span>
                  </div>
                  {selectedHomePage === page.id && <span className="option-check">✓</span>}
                </div>
              ))}
            </div>
          </section>

          {/* Theme Selection */}
          <section className="settings-section">
            <h2>🎨 Theme</h2>
            <p className="section-description">Choose your preferred color theme</p>
            
            <div className="themes-grid">
              {themes.map((theme) => (
                <div
                  key={theme.id}
                  className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''}`}
                  onClick={() => handleThemeChange(theme.id)}
                >
                  <div className="theme-icon">{theme.icon}</div>
                  <h3>{theme.name}</h3>
                  <p>{theme.description}</p>
                  <div className="theme-preview">
                    <span style={{ backgroundColor: theme.preview.primary }}></span>
                    <span style={{ backgroundColor: theme.preview.secondary }}></span>
                    <span style={{ backgroundColor: theme.preview.background }}></span>
                    <span style={{ backgroundColor: theme.preview.text }}></span>
                  </div>
                  {selectedTheme === theme.id && (
                    <div className="selected-badge">✓ Active</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Password Change */}
          <section className="settings-section">
            <h2>🔒 Change Password</h2>
            <p className="section-description">Update your account password</p>
            
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  >
                    {showPasswords.current ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  >
                    {showPasswords.new ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {/* Password Strength Indicator */}
                {passwordData.newPassword && (
                  <div className="password-strength-container">
                    <div className="password-strength-bar">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className="strength-segment"
                          style={{
                            backgroundColor: level <= getPasswordStrength(passwordData.newPassword).level 
                              ? getPasswordStrength(passwordData.newPassword).color 
                              : 'var(--border-color)'
                          }}
                        />
                      ))}
                    </div>
                    <span className="strength-label" style={{ color: getPasswordStrength(passwordData.newPassword).color }}>
                      {getPasswordStrength(passwordData.newPassword).label}
                    </span>
                  </div>
                )}
                {/* Password Requirements Checklist */}
                {passwordData.newPassword && validatePassword(passwordData.newPassword).length > 0 && (
                  <div className="password-requirements">
                    <small>Requirements:</small>
                    <ul>
                      {validatePassword(passwordData.newPassword).map((req, idx) => (
                        <li key={idx} className="requirement-missing">✗ {req}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  >
                    {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {/* Password Match Indicator */}
                {passwordData.confirmPassword && (
                  <div className="password-match-indicator">
                    {passwordData.newPassword === passwordData.confirmPassword ? (
                      <span className="match-success">✓ Passwords match</span>
                    ) : (
                      <span className="match-error">✗ Passwords do not match</span>
                    )}
                  </div>
                )}
              </div>

              {passwordMessage.text && (
                <div className={`password-message ${passwordMessage.type}`}>
                  {passwordMessage.text}
                </div>
              )}

              <button type="submit" className="btn-primary" disabled={isChangingPassword}>
                {isChangingPassword ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </section>

          {/* Pause Account Section */}
          <section className="settings-section">
            <h2>⏸️ Account Pause</h2>
            <p className="section-description">
              Take a break from matchmaking without deactivating your account
            </p>
            
            {pauseStatus?.isPaused ? (
              <div className="pause-active-card">
                <div className="pause-active-header">
                  <span className="pause-icon">⏸️</span>
                  <div className="pause-info">
                    <strong>Your account is currently PAUSED</strong>
                    <p className="pause-details">
                      Paused {pauseStatus.pausedAt && `on ${new Date(pauseStatus.pausedAt).toLocaleDateString()}`}
                      {pauseStatus.pausedUntil && (
                        <> • Auto-unpause on {new Date(pauseStatus.pausedUntil).toLocaleDateString()}</>
                      )}
                    </p>
                    {pauseStatus.pauseReason && (
                      <p className="pause-reason">Reason: {pauseStatus.pauseReason.replace(/_/g, ' ')}</p>
                    )}
                  </div>
                </div>
                
                <div className="pause-effects-summary">
                  <p>While paused:</p>
                  <ul>
                    <li>❌ Hidden from all searches</li>
                    <li>❌ No new matches generated</li>
                    <li>❌ Cannot send/receive messages</li>
                    <li>✅ Can still edit your profile</li>
                    <li>✅ Subscription remains active</li>
                  </ul>
                </div>
                
                <button
                  className="btn-unpause"
                  onClick={handleUnpause}
                >
                  ▶️ Un-Pause My Account
                </button>
              </div>
            ) : (
              <div className="pause-inactive-card">
                <p className="pause-description">
                  Need a break? Pause your account to:
                </p>
                <ul className="pause-benefits">
                  <li>✅ Temporarily hide from searches and matches</li>
                  <li>✅ Stop receiving messages (read-only access)</li>
                  <li>✅ Keep your subscription and profile data</li>
                  <li>✅ Un-pause anytime you want to return</li>
                </ul>
                <button
                  className="btn-configure-pause"
                  onClick={() => setShowPauseModal(true)}
                >
                  ⏸️ Configure Pause Settings
                </button>
              </div>
            )}
          </section>

          {/* Data Export Section */}
          <section className="settings-section">
            <h2>� Data Export</h2>
            <p className="section-description">Download a copy of your personal data</p>
            
            <div className="data-export-section">
              <p className="data-export-description">
                Download all your profile data, messages, matches, and activity history in JSON format. 
                This includes everything we have stored about your account.
              </p>
              <button 
                className="btn-secondary download-data-btn"
                onClick={handleDownloadData}
              >
                📦 Download My Data (GDPR)
              </button>
            </div>
          </section>

          {/* DANGER ZONE - Account Deletion */}
          <section className="settings-section danger-zone-section">
            <h2 className="danger-zone-title">🗑️ Delete Account</h2>
            <div className="danger-zone-warning">
              <strong>⚠️ Warning: This starts a 30-day deletion process</strong>
              <p>Deleting your account will:</p>
              <ul>
                <li>Hide your profile from all searches immediately</li>
                <li>Delete all your messages and matches after 30 days</li>
                <li>Remove your photos and personal information</li>
                <li>You can reactivate anytime within 30 days by logging back in</li>
              </ul>
            </div>
            
            <button 
              className="btn btn-danger btn-lg delete-account-btn"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete My Account
            </button>
          </section>
        </div>
            )
          },
          {
            id: 'security',
            icon: '🔐',
            label: 'MFA',
            content: (
              <div className="security-settings">
          {/* MFA Status Banner */}
          <section className="settings-section">
            <h2>🔐 Multi-Factor Authentication (MFA)</h2>
            <p className="section-description">Add an extra layer of security to your account</p>
            
            {/* Current Status */}
            <div className={`mfa-status-banner ${mfaStatus.mfa_enabled ? 'enabled' : 'disabled'}`}>
              <div className="status-icon">
                {mfaStatus.mfa_enabled ? '✅' : '⚪'}
              </div>
              <div className="status-info">
                <strong>{mfaStatus.mfa_enabled ? 'MFA Enabled' : 'MFA Disabled'}</strong>
                <p>
                  {mfaStatus.mfa_enabled 
                    ? `Active via ${mfaStatus.mfa_channel?.toUpperCase()} - ${mfaStatus.contact_masked}`
                    : 'Your account is not protected by MFA'
                  }
                </p>
                {mfaStatus.mfa_enabled && (
                  <p className="backup-codes-info">
                    {mfaStatus.backup_codes_count} backup codes remaining
                  </p>
                )}
              </div>
            </div>

            {/* MFA Messages */}
            {mfaMessage.text && (
              <div className={`alert alert-${mfaMessage.type} up-mt-16`} >
                {mfaMessage.text}
              </div>
            )}

            {/* MFA Setup/Management */}
            {!mfaStatus.mfa_enabled ? (
              <>
                {/* Step 1: Select Method */}
                {mfaStep === 'select' && (
                  <div className="mfa-setup">
                    <h3>Choose MFA Method</h3>
                    <div className="mfa-options">
                      <label className={`mfa-option ${selectedMfaMethod === 'none' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="mfa-method"
                          value="none"
                          checked={selectedMfaMethod === 'none'}
                          onChange={() => handleMfaMethodChange('none')}
                        />
                        <div className="option-content">
                          <span className="option-icon">⚪</span>
                          <div>
                            <strong>None (Disabled)</strong>
                            <p>No additional security</p>
                          </div>
                        </div>
                      </label>

                      <label className={`mfa-option ${selectedMfaMethod === 'email' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="mfa-method"
                          value="email"
                          checked={selectedMfaMethod === 'email'}
                          onChange={() => handleMfaMethodChange('email')}
                        />
                        <div className="option-content">
                          <span className="option-icon">📧</span>
                          <div>
                            <strong>Email OTP</strong>
                            <p>Receive codes via email</p>
                          </div>
                        </div>
                      </label>

                      <label className={`mfa-option ${selectedMfaMethod === 'sms' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="mfa-method"
                          value="sms"
                          checked={selectedMfaMethod === 'sms'}
                          onChange={() => setSelectedMfaMethod('sms')}
                        />
                        <div className="option-content">
                          <span className="option-icon">📱</span>
                          <div>
                            <strong>SMS OTP</strong>
                            <p>Receive codes via text message</p>
                          </div>
                        </div>
                      </label>
                      
                      {selectedMfaMethod === 'sms' && (!smsOptIn || !hasPhoneNumber) && (
                        <div className="mfa-warning mfa-notice">
                          <strong>⚠️ {!hasPhoneNumber ? 'Phone Number Required' : 'SMS Notifications Disabled'}</strong>
                          <p className="up-mt-8 up-text-sm" >
                            {!hasPhoneNumber 
                              ? 'You need to add a phone number to your profile before enabling SMS MFA. Please add your phone number in Profile Edit.'
                              : 'You have SMS notifications disabled in your profile. To use SMS MFA, please enable SMS notifications in the profile first.'
                            }
                          </p>
                          <button
                            onClick={() => {
                              navigate('/edit-profile');
                            }}
                            className="up-btn-warn" 
                          >
                            Go to Profile Edit
                          </button>
                        </div>
                      )}
                    </div>

                    {selectedMfaMethod !== 'none' && (
                      <div className="mfa-info-box">
                        <p>ℹ️ You'll receive a verification code to confirm ownership before MFA is enabled.</p>
                      </div>
                    )}

                    <button
                      className="btn-primary"
                      onClick={handleSendMfaCode}
                      disabled={loadingMfa || selectedMfaMethod === 'none' || (selectedMfaMethod === 'sms' && (!smsOptIn || !hasPhoneNumber))}
                    >
                      {loadingMfa ? 'Sending Code...' : 'Enable MFA'}
                    </button>
                  </div>
                )}

                {/* Step 2: Verify Code */}
                {mfaStep === 'verify' && (
                  <div className="mfa-verify">
                    <h3>Enter Verification Code</h3>
                    <p>We sent a 6-digit code to your {selectedMfaMethod}</p>
                    
                    <input
                      type="text"
                      className="verification-code-input mfa-code-input"
                      placeholder="000000"
                      maxLength="6"
                      value={mfaVerificationCode}
                      onChange={(e) => setMfaVerificationCode(e.target.value.replace(/\D/g, ''))}
                      
                    />

                    <div className="button-group">
                      <button
                        className="btn-primary"
                        onClick={handleVerifyAndEnableMfa}
                        disabled={loadingMfa || mfaVerificationCode.length !== 6}
                      >
                        {loadingMfa ? 'Verifying...' : 'Verify & Enable'}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={handleSendMfaCode}
                        disabled={loadingMfa}
                      >
                        Resend Code
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setMfaStep('select');
                          setMfaVerificationCode('');
                          setMfaMessage({ type: '', text: '' });
                        }}
                        disabled={loadingMfa}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Backup Codes */}
                {mfaStep === 'backup_codes' && (
                  <div className="mfa-backup-codes">
                    <h3>⚠️ Save Your Backup Codes</h3>
                    <p className="warning-text">
                      Store these codes in a safe place. Each code can only be used once to access your account if you lose access to your {selectedMfaMethod}.
                    </p>

                    <div className="backup-codes-grid">
                      {backupCodes.map((code, index) => (
                        <div key={index} className="backup-code">
                          {code}
                        </div>
                      ))}
                    </div>

                    <div className="button-group">
                      <button className="btn-primary" onClick={copyBackupCodes}>
                        📋 Copy Codes
                      </button>
                      <button className="btn-primary" onClick={downloadBackupCodes}>
                        💾 Download Codes
                      </button>
                      <button className="btn-secondary" onClick={handleFinishMfaSetup}>
                        I've Saved My Codes
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* MFA Enabled - Show Management Options */
              <div className="mfa-management">
                <div className="button-group">
                  <button
                    className="btn-danger"
                    onClick={() => setShowDisableMfaModal(true)}
                  >
                    Disable MFA
                  </button>
                </div>

                {/* Disable MFA Modal */}
                {showDisableMfaModal && (
                  <div className="modal-overlay" onClick={() => setShowDisableMfaModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h3>Disable MFA</h3>
                      <p>Enter your password to confirm</p>
                      
                      <input
                        type="password"
                        placeholder="Password"
                        value={disableMfaPassword}
                        onChange={(e) => setDisableMfaPassword(e.target.value)}
                        className="up-input-full" 
                      />

                      <div className="button-group">
                        <button
                          className="btn-danger"
                          onClick={handleDisableMfa}
                          disabled={loadingMfa}
                        >
                          {loadingMfa ? 'Disabling...' : 'Disable MFA'}
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => {
                            setShowDisableMfaModal(false);
                            setDisableMfaPassword('');
                          }}
                          disabled={loadingMfa}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
            )
          },
          /* Contributions tab hidden in the native app — no payment
             surfaces allowed (Google Play billing policy) */
          ...(!isNativePlatform() ? [{
            id: 'contributions',
            icon: '💝',
            label: 'Contributions',
            content: (
              <div className="contributions-settings">
                {/* Inline Contribution Form (reuses ContributionPopup) */}
                <ContributionPopup 
                  embedded={true}
                  isOpen={true}
                  onClose={() => {}}
                  onSuccess={() => {
                    loadContributionHistory();
                    showToast('Contribution successful! Thank you for your support.', 'success');
                  }}
                />

                {/* Contribution History (below form) */}
                <section className="settings-section up-mt-32">
                  <h2>💝 Contribution History</h2>
                  <p className="section-description">Your past contributions</p>
                  
                  {loadingContributionHistory ? (
                    <p>Loading contribution history...</p>
                  ) : contributionHistory.length > 0 ? (
                    <div className="contribution-history-list">
                      {contributionHistory.slice(0, 2).map((contribution, index) => (
                        <div key={index} className="contribution-history-item">
                          <span className="contribution-history-date">
                            {new Date(contribution.date).toLocaleDateString()}
                          </span>
                          <span className="contribution-history-type">
                            {contribution.paymentType === 'recurring' ? 'Monthly Recurring' : 'One-time'}
                          </span>
                          <span className="contribution-history-amount">
                            ${contribution.amount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="last-contribution-card">
                      <p className="up-center up-muted" >
                        No contributions yet. Your support helps keep the platform running!
                      </p>
                    </div>
                  )}
                </section>

              </div>
            )
          }] : []),
          {
            id: 'notifications',
            icon: '🔔',
            label: 'Notifications ',
            content: notificationPreferences && (
              <div className="notification-settings">
          {/* Push Notification Status */}
          <section className="settings-section push-status-section">
            <h2>📲 Push Notifications</h2>
            <p className="section-description">Receive instant browser notifications</p>
            
            <div className="push-status-card up-card up-mt-16">
              <div className="up-row-between up-row-wrap" >
                <div>
                  <div className="up-row-12 up-mb-8" >
                    <span className="up-icon-15" >
                      {pushPermission === 'granted' ? '✅' : pushPermission === 'denied' ? '🚫' : '⏳'}
                    </span>
                    <strong className="up-icon-11" >
                      {pushPermission === 'granted' ? 'Push Enabled' : 
                       pushPermission === 'denied' ? 'Push Blocked' : 
                       pushPermission === 'unsupported' ? 'Not Supported' : 'Push Not Enabled'}
                    </strong>
                  </div>
                  <p className="up-muted up-text-sm" >
                    {pushPermission === 'granted' 
                      ? 'You will receive browser notifications for enabled events.'
                      : pushPermission === 'denied'
                      ? 'Push notifications are blocked. Enable in browser settings.'
                      : pushPermission === 'unsupported'
                      ? 'Your browser does not support push notifications.'
                      : 'Enable push notifications to receive instant alerts.'}
                  </p>
                </div>
                
                <div className="up-btn-row" >
                  {pushPermission !== 'granted' && pushPermission !== 'unsupported' && (
                    <button
                      className="btn-primary up-minw-140"
                      onClick={handleEnablePush}
                      disabled={enablingPush}
                      
                    >
                      {enablingPush ? '⏳ Enabling...' : '🔔 Enable Push'}
                    </button>
                  )}
                  
                  {pushPermission === 'granted' && (
                    <button
                      className="btn-secondary up-minw-140"
                      onClick={handleTestPush}
                      disabled={testingPush}
                      
                    >
                      {testingPush ? '⏳ Sending...' : '🧪 Test Push'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Notification Channels */}
          <section className="settings-section">
            <h2>🔔 Notification Channels</h2>
            <p className="section-description">Choose how you want to be notified for different events</p>

            {Object.entries(notificationTriggers).map(([category, triggers]) => (
              <div key={category} className="trigger-category">
                <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                {triggers.map((trigger) => (
                  <div key={trigger.id} className="trigger-row">
                    <div className="trigger-info">
                      <strong>{trigger.label}</strong>
                      <small>{trigger.description}</small>
                    </div>
                    <div className="channel-toggles">
                      {channels.map((channel) => (
                        <label
                          key={channel}
                          className={`channel-toggle ${isActivityChannelBlocked(trigger.id, channel) ? 'disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={notificationPreferences.channels[trigger.id]?.includes(channel)}
                            disabled={isActivityChannelBlocked(trigger.id, channel)}
                            onChange={() => handleChannelToggle(trigger.id, channel)}
                          />
                          <span className={`channel-icon ${channel} ${isActivityChannelBlocked(trigger.id, channel) ? 'disabled' : ''}`}>
                            {channel === 'email' && '📧'}
                            {channel === 'sms' && '📱'}
                            {channel === 'push' && '🔔'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </section>

          {/* Quiet Hours */}
          <section className="settings-section">
            <h2>🌙 Quiet Hours</h2>
            <p className="section-description">Set Do Not Disturb hours</p>

            <div className="quiet-hours-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={notificationPreferences.quietHours.enabled}
                  onChange={handleQuietHoursToggle}
                />
                <span>Enable Quiet Hours</span>
              </label>
            </div>

            {notificationPreferences.quietHours.enabled && (
              <div className="quiet-hours-settings">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={notificationPreferences.quietHours.start}
                    onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={notificationPreferences.quietHours.end}
                    onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <input
                    type="text"
                    value={notificationPreferences.quietHours.timezone}
                    onChange={(e) => handleQuietHoursChange('timezone', e.target.value)}
                    placeholder="UTC"
                  />
                </div>
              </div>
            )}
          </section>

          {/* Daily Digest Settings */}
          <section className="settings-section digest-settings-section">
            <h2>📬 Daily Digest</h2>
            <p className="section-description">
              Receive a single daily email summarizing all your activity instead of individual notifications
            </p>

            {/* Main Toggle */}
            <div className={`digest-main-toggle ${digestSettings.enabled ? 'on' : ''}`}>
              <div>
                <strong className="up-row up-icon-11" >
                  {digestSettings.enabled ? '✅' : '📧'} Daily Digest Email
                </strong>
                <p className="up-muted up-text-sm up-mt-6" >
                  {digestSettings.enabled 
                    ? 'Enabled - You\'ll receive a daily digest at 8:00 AM UTC'
                    : 'Disabled - You receive individual notifications immediately'}
                </p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={digestSettings.enabled}
                  onChange={() => handleDigestToggle('enabled')}
                />
                <span className="slider round"></span>
              </label>
            </div>

            {/* Digest Settings (shown when enabled) */}
            {digestSettings.enabled && (
              <div className="digest-options up-card up-mb-20">
                {/* Skip if no activity */}
                <div className="up-toggle-row up-mb-16" >
                  <div>
                    <strong>Skip if no activity</strong>
                    <p className="up-muted-sm up-mt-4" >
                      Don't send digest if there's nothing new to report
                    </p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={digestSettings.skipIfNoActivity}
                      onChange={() => handleDigestToggle('skipIfNoActivity')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                {/* Batch Settings Header */}
                <h4 className="up-section-head" >
                  🔄 Batch These Into Digest
                  <span className="up-muted-xs" >
                    (instead of sending immediately)
                  </span>
                </h4>

                {/* Batch Toggles */}
                <div className="batch-toggles up-col">
                  {[
                    { key: 'batchFavorites', icon: '⭐', label: 'Favorited By', desc: 'When someone favorites you' },
                    { key: 'batchShortlists', icon: '📋', label: 'Shortlisted By', desc: 'When someone shortlists you' },
                    { key: 'batchProfileViews', icon: '👁️', label: 'Profile Views', desc: 'When someone views your profile' },
                    { key: 'batchNewMatches', icon: '🦋', label: 'New L3V3L Matches', desc: 'New high-scoring matches' },
                    { key: 'batchPiiRequests', icon: '🔐', label: 'PII Requests', desc: 'Contact info requests (not recommended)', warning: true }
                  ].map(item => (
                    <div key={item.key} className={`batch-toggle-row ${item.warning && digestSettings[item.key] ? 'warn' : ''}`}>
                      <div className="up-row-12" >
                        <span className="up-icon-12" >{item.icon}</span>
                        <div>
                          <strong>{item.label}</strong>
                          <p className="up-muted-sm up-mt-2" >
                            {item.desc}
                          </p>
                        </div>
                      </div>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={digestSettings[item.key]}
                          onChange={() => handleDigestToggle(item.key)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Info Note */}
                <div className="up-note-info up-mt-20" >
                  <strong>💡 How it works:</strong> When enabled, the selected notification types will be collected and sent in your daily digest email instead of being sent immediately. This reduces email clutter while keeping you informed.
                </div>
              </div>
            )}
          </section>

          {/* Privacy Settings - Premium Feature (visible only for non-free users) */}
          {userRole !== 'free_user' && (
            <section className="settings-section privacy-settings-section">
              <h2>🔒 Privacy Settings</h2>
              <p className="section-description">
                Control whether others are notified about your activity
                <span className="up-badge-ok" >
                  Premium
                </span>
              </p>

              <div className="privacy-toggles up-mt-16">
                <div className="privacy-toggle-row up-toggle-row up-mb-12">
                  <div>
                    <strong>Hide Favorites</strong>
                    <p className="up-muted-sm up-mt-4" >
                      Others won't know when you favorite them
                    </p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={privacySettings.hideFavorites}
                      onChange={() => handlePrivacyToggle('hideFavorites')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="privacy-toggle-row up-toggle-row up-mb-12">
                  <div>
                    <strong>Hide Shortlist</strong>
                    <p className="up-muted-sm up-mt-4" >
                      Others won't know when you shortlist them
                    </p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={privacySettings.hideShortlist}
                      onChange={() => handlePrivacyToggle('hideShortlist')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="privacy-toggle-row up-toggle-row">
                  <div>
                    <strong>Hide Profile Views</strong>
                    <p className="up-muted-sm up-mt-4" >
                      Others won't know when you view their profile
                    </p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={privacySettings.hideProfileViews}
                      onChange={() => handlePrivacyToggle('hideProfileViews')}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="privacy-toggle-row up-toggle-row">
                  <div>
                    <strong>Share Contact with Paid Members</strong>
                    <p className="up-muted-sm up-mt-4" >
                      Paid members see your phone, email &amp; LinkedIn instantly — off = manual approval
                    </p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={shareContactWithPaidMembers}
                      onChange={handleShareContactToggle}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>

              <div className="up-note-info" >
                <strong>ℹ️ Note:</strong> When enabled, your actions will still be recorded but the other person won't receive any notification about it.
              </div>
            </section>
          )}

          {/* Action Buttons */}
          <div className="notification-actions">
            <button
              className="btn-primary"
              onClick={handleSaveNotifications}
              disabled={savingNotifications}
            >
              {savingNotifications ? 'Saving...' : 'Save Notification Settings'}
            </button>
            <button
              className={`btn-secondary ${resetArmed ? 'confirm-armed' : ''}`}
              onClick={handleResetNotifications}
              disabled={savingNotifications}
            >
              {resetArmed ? '⚠️ Click again to confirm reset' : 'Reset to Defaults'}
            </button>
          </div>
        </div>
            )
          },
          ] : []),
          ...(isAdmin && includeAdminTab ? [{
            id: 'admin',
            icon: '⚙️',
            label: 'SysConfig',
            content: (
              <div className="admin-settings">
          <h2>⚙️ System Configuration</h2>
          <p className="section-description">Configure global system settings and preferences</p>
          
          {/* Backend Services Status */}
          <SystemStatus />

          {/* Unified save bar for all system settings below */}
          {!adminSettingsLoading && (
            <div className={`up-save-bar ${systemSettingsDirty ? 'dirty' : ''}`}>
              <span className="up-save-bar-status">
                {systemSettingsDirty ? '● Unsaved changes' : '✓ All changes saved'}
              </span>
              <div className="up-save-bar-actions">
                {systemSettingsDirty && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleDiscardSystemSettings}
                    disabled={savingSystemSettings}
                  >
                    Discard
                  </button>
                )}
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveSystemSettings}
                  disabled={savingSystemSettings || !systemSettingsDirty}
                >
                  {savingSystemSettings ? '💾 Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>
          )}

          <h4 className="up-group-title">🗄️ Data Retention</h4>

          {adminSettingsLoading ? (
            <div className="up-center up-pad-40" >
              <div className="loading-spinner"></div>
              <p>Loading system settings...</p>
            </div>
          ) : (
            <div className="settings-card">
              <h3>🎫 Ticket Management</h3>
              <p>Configure automatic cleanup for resolved support tickets</p>
              
              <div className="form-group up-mt-24">
                <div className="up-row up-mb-8" >
                  <label htmlFor="ticketDeleteDays" className="up-fw-600" >Auto-Delete Period</label>
                  <Tooltip
                    icon
                    text={
                      <>
                        <strong className="up-block up-mb-8" >How it works:</strong>
                        <ul className="up-list" >
                          <li>When a ticket is marked as <strong>resolved</strong> or <strong>closed</strong>, a deletion timestamp is set</li>
                          <li>A background job runs every hour to delete tickets past their scheduled deletion time</li>
                          <li>All attachments are permanently deleted from the filesystem</li>
                        </ul>
                      </>
                    }
                  />
                </div>
                <p className="up-muted up-text-sm up-mb-12" >
                  Resolved/closed tickets and attachments will be automatically deleted after this period.
                </p>
                
                <div className="up-row-wrap" >
                  <select
                    id="ticketDeleteDays"
                    value={ticketDeleteDays}
                    onChange={(e) => setTicketDeleteDays(Number(e.target.value))}
                    disabled={savingSystemSettings}
                    className="form-control up-field"
                  >
                    <option value={0}>Immediately (on resolve/close)</option>
                    <option value={7}>7 days after resolved</option>
                    <option value={14}>14 days after resolved</option>
                    <option value={30}>30 days after resolved (Recommended)</option>
                    <option value={60}>60 days after resolved</option>
                    <option value={90}>90 days after resolved</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Profile View History Settings */}
          {!adminSettingsLoading && (
            <div className="settings-card up-mt-24">
              <h3>👁️ Profile View History</h3>
              <p>Configure how long profile view history is visible to users</p>

              <div className="form-group up-mt-24">
                <div className="up-row up-mb-8" >
                  <label htmlFor="profileViewHistoryDays" className="up-fw-600" >View History Retention</label>
                  <Tooltip
                    icon
                    text={
                      <>
                        <strong className="up-block up-mb-8" >How it works:</strong>
                        <ul className="up-list" >
                          <li>Users can see who viewed their profile within this time period</li>
                          <li>Older views are hidden but not deleted from the database</li>
                          <li>Helps manage privacy and prevents stalking behavior</li>
                          <li>Recommended: 7-14 days for active engagement</li>
                        </ul>
                      </>
                    }
                  />
                </div>
                <p className="up-muted up-text-sm up-mb-12" >
                  Users will only see profile views from the last N days on their dashboard.
                </p>
                
                <div className="up-row-wrap" >
                  <select
                    id="profileViewHistoryDays"
                    value={profileViewHistoryDays}
                    onChange={(e) => setProfileViewHistoryDays(Number(e.target.value))}
                    disabled={savingSystemSettings}
                    className="form-control up-field"
                  >
                    <option value={1}>1 day (24 hours)</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days (Recommended)</option>
                    <option value={14}>14 days (2 weeks)</option>
                    <option value={30}>30 days (1 month)</option>
                    <option value={60}>60 days (2 months)</option>
                    <option value={90}>90 days (3 months)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <h4 className="up-group-title">🚩 Feature Toggles</h4>

          {/* L3V3L Algorithm Feature Toggle */}
          {!adminSettingsLoading && (
            <div className="settings-card up-mt-24">
              <h3>🦋 L3V3L Compatibility Algorithm</h3>
              <p>Control access to AI-powered compatibility scoring feature</p>
              
              <div className="form-group up-mt-24">
                <div className="up-row-16-hover" >
                  <div className="up-flex-1" >
                    <label htmlFor="enableL3V3L" className="up-fw-600 up-block up-mb-4" >
                      Enable L3V3L for All Users
                    </label>
                    <p className="up-muted up-text-sm" >
                      When enabled, all users can filter search results by L3V3L compatibility score. Disable to make this a premium-only feature.
                    </p>
                  </div>
                  <label className="switch up-ml-auto">
                    <input
                      id="enableL3V3L"
                      type="checkbox"
                      checked={enableL3V3LForAll}
                      onChange={(e) => setEnableL3V3LForAll(e.target.checked)}
                      disabled={savingSystemSettings}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                
                <div className={`status-note ${enableL3V3LForAll ? 'on' : 'warn'}`}>
                  <strong>Current Status:</strong> {enableL3V3LForAll ? '✅ Available to all users (Testing Phase)' : '🔒 Premium users only'}
                  <br />
                  <span className="up-muted" >
                    {enableL3V3LForAll 
                      ? 'Use this setting to collect user feedback before deciding on premium monetization.' 
                      : 'Only users with premium subscription can use L3V3L compatibility filtering.'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Profile Picture Visibility Settings */}
          {!adminSettingsLoading && (
            <div className="settings-card up-mt-24">
              <h3>📷 Profile Picture Visibility</h3>
              <p>Control whether the profile picture (first image) is always visible to members</p>

              <div className="form-group up-mt-24">
                <div className="up-row-16-hover" >
                  <div className="up-flex-1" >
                    <label htmlFor="profilePictureAlwaysVisible" className="up-fw-600 up-block up-mb-4" >
                      Profile Picture Always Visible
                      <Tooltip
                        icon
                        text={
                          <>
                            <strong className="up-block up-mb-8" >Industry Standard Practice:</strong>
                            <ul className="up-list" >
                              <li><strong>When ON:</strong> Profile picture (first image) is always visible to logged-in members</li>
                              <li><strong>When OFF:</strong> Profile picture follows same privacy rules as other photos</li>
                              <li>Major platforms (Shaadi, BharatMatrimony) show profile pics by default</li>
                              <li>Users expect to see at least one photo before engaging</li>
                              <li>Additional photos still respect privacy settings</li>
                            </ul>
                          </>
                        }
                      />
                    </label>
                    <p className="up-muted up-text-sm" >
                      When enabled, the profile picture is visible to all members regardless of privacy settings.
                    </p>
                  </div>
                  <label className="switch up-ml-auto">
                    <input
                      id="profilePictureAlwaysVisible"
                      type="checkbox"
                      checked={profilePictureAlwaysVisible}
                      onChange={(e) => setProfilePictureAlwaysVisible(e.target.checked)}
                      disabled={savingSystemSettings}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <div className="up-note-info" >
                  <p className="up-text-sm" >
                    <strong>Current Status:</strong> {profilePictureAlwaysVisible ? '👁️ Profile pictures are visible to all members' : '🔒 Profile pictures follow individual privacy settings'}
                  </p>
                  <p className="up-muted-sm up-mt-8" >
                    This setting only affects the first image (profile picture). Additional photos always respect the user's privacy settings.
                  </p>
                </div>
              </div>
            </div>
          )}

          <h4 className="up-group-title">💝 Engagement</h4>

          {/* Contribution Popup Settings */}
          {!adminSettingsLoading && (
            <div className="settings-card up-mt-24">
              <h3>💝 Contribution Popup</h3>
              <p>Configure the contribution popup that encourages users to support the platform</p>

              <div className="form-group up-mt-24">
                <div className="up-row-16-hover" >
                  <div className="up-flex-1" >
                    <label htmlFor="contributionEnabled" className="up-fw-600 up-block up-mb-4" >
                      Enable Contribution Popup
                      <Tooltip
                        icon
                        text={
                          <>
                            <strong className="up-block up-mb-8" >Contribution Popup Settings:</strong>
                            <ul className="up-list" >
                              <li><strong>Enabled:</strong> Shows popup to eligible users asking for contributions</li>
                              <li><strong>Min Logins:</strong> User must have logged in this many times before seeing popup</li>
                              <li><strong>Frequency:</strong> Days between showing popup to same user</li>
                              <li>Users with active recurring contributions won't see the popup</li>
                              <li>Admins can disable popup for individual users in User Management</li>
                            </ul>
                          </>
                        }
                      />
                    </label>
                    <p className="up-muted up-text-sm" >
                      Shows a contribution request to eligible users at a controlled frequency.
                    </p>
                  </div>
                  <label className="switch up-ml-auto">
                    <input
                      id="contributionEnabled"
                      type="checkbox"
                      checked={contributionEnabled}
                      onChange={(e) => setContributionEnabled(e.target.checked)}
                      disabled={savingContributionSettings}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>

                <button
                  onClick={handleSaveContributionSettings}
                  disabled={savingContributionSettings}
                  className="btn-primary up-nowrap"
                >
                  {savingContributionSettings ? '💾 Saving...' : '💾 Save Contribution Settings'}
                </button>

                <div className={`status-note ${contributionEnabled ? 'on' : ''}`}>
                  <p className="up-text-sm" >
                    <strong>Current Status:</strong> {contributionEnabled
                      ? '💝 Popup enabled - Popup will show to eligible users (non-admin/moderator, not inside silence window)'
                      : '🔕 Popup disabled - Users will not see contribution requests'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
            )
          }] : [])
        ]}
      />

      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.type === 'success' && '✓ '}
          {toast.type === 'error' && '✕ '}
          {toast.message}
        </div>
      )}

      {/* Pause Settings Modal */}
      <PauseSettings
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onPause={handlePauseSuccess}
        currentStatus={pauseStatus}
      />

      {/* Account Deletion Confirmation Modal */}
      {showDeleteModal && (
        <div 
          className="deletion-modal-overlay" 
          onClick={() => setShowDeleteModal(false)}
        >
          <div 
            className="deletion-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="deletion-modal-title">
              Are you absolutely sure?
            </h3>
            <p className="deletion-modal-description">
              This will start a 30-day deletion process. You can cancel anytime during this period by logging back in.
            </p>
            
            <div className="deletion-modal-options">
              <label className="deletion-modal-checkbox-label">
                <input 
                  type="checkbox" 
                  checked={downloadData}
                  onChange={(e) => setDownloadData(e.target.checked)}
                  className="deletion-modal-checkbox"
                />
                <span>Download my data before deletion</span>
              </label>
            </div>
            
            <div className="deletion-modal-form-group">
              <label className="deletion-modal-label">
                Why are you leaving? (Optional)
              </label>
              <textarea 
                className="deletion-modal-textarea"
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                placeholder="Tell us why you're leaving..."
                rows={3}
              />
            </div>
            
            <div className="deletion-modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletionReason('');
                  setDownloadData(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleRequestDeletion}
              >
                Yes, Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedPreferences;
